/* AMAHC identity capture — theme snippet.
 *
 * Drop into the LIVE theme just before </body> (resolve the live theme id with
 * `shopify theme list`; push with --allow-live --only). Depends on nothing: no jQuery, no
 * bundler, ~2KB. Safe to load twice (guards on window.__amahcId).
 *
 * What it does, and why each piece exists:
 *
 *  1. Mints a durable first-party visitor id (vid) and keeps it in BOTH a first-party cookie
 *     and localStorage. Safari's ITP caps JS-set cookies at 7 days, so the cookie alone would
 *     silently forget a visitor who returns in week two — localStorage is not capped the same
 *     way, so the pair survives far longer than either alone. We also seed from Shopify's own
 *     server-set _shopify_y when present, because a server-set cookie outlives anything JS can
 *     create, giving us the most durable id available on this platform.
 *
 *  2. Captures _fbp / _fbc, rebuilding _fbc from a fbclid still in the URL when fbevents.js
 *     has not written the cookie yet (a fast click-through from an ad).
 *
 *  3. Sends identity to /api/launcher/track — the endpoint the launcher ALREADY beacons to —
 *     on: any form submit carrying an email/phone, the launcher's own call/chat taps, and once
 *     per session as an anonymous identity ping so a vid+fbc pair exists even for a visitor who
 *     never fills anything in. That anonymous row is what a phone order weeks later matches on.
 *
 * PII: email/phone leave the browser over HTTPS to our own endpoint, which hashes the email
 * before storing it in analytics and puts the raw value only in the identity spine. Nothing is
 * written to the URL, and nothing is sent to any third party from here.
 */
(function () {
  if (window.__amahcId) return;
  window.__amahcId = true;

  /* Never run on theme previews or Shopify's own preview hosts. Events Manager has been seeing
     data from *.shopifypreview.com since June; staff previewing a theme is not a customer, and
     their sessions should not enter the identity spine or the pixel. Cheap guard, and it keeps
     our own data clean even though we cannot stop Shopify's channel pixel doing the same. */
  try {
    if (/shopifypreview\.com$/i.test(location.hostname) ||
        /[?&]preview_theme_id=/.test(location.search) ||
        /[?&]_ab=|[?&]_fd=|[?&]_sc=/.test(location.search)) return;
  } catch (e) {}

  var TRACK = 'https://stock-cost-dashboard.vercel.app/api/launcher/track';
  var VID_KEY = 'amahc_vid';
  var ONE_YEAR = 60 * 60 * 24 * 365;

  function ck(n) {
    var m = document.cookie.match('(^|;)\\s*' + n + '\\s*=\\s*([^;]+)');
    return m ? m.pop() : '';
  }
  function setCk(n, v) {
    try {
      document.cookie = n + '=' + v + ';path=/;max-age=' + ONE_YEAR + ';SameSite=Lax' +
        (location.protocol === 'https:' ? ';Secure' : '');
    } catch (e) {}
  }
  function ls(k, v) {
    try { if (v === undefined) return localStorage.getItem(k); localStorage.setItem(k, v); } catch (e) { return null; }
  }

  /* One id, three possible homes. Prefer whichever already exists so a returning visitor keeps
     their identity; only mint when all three are empty. Shopify's _shopify_y is server-set and
     therefore the most durable seed available to us. */
  function vid() {
    var v = ck(VID_KEY) || ls(VID_KEY) || '';
    if (!v) {
      var shopifyY = ck('_shopify_y');
      v = shopifyY || (Date.now().toString(36) + Math.random().toString(36).slice(2, 10));
    }
    setCk(VID_KEY, v);
    ls(VID_KEY, v);
    return v;
  }

  function meta() {
    var fbp = ck('_fbp'), fbc = ck('_fbc');
    if (!fbc) {
      try {
        var cid = new URLSearchParams(location.search).get('fbclid');
        if (cid) {
          fbc = 'fb.1.' + Date.now() + '.' + cid;
          setCk('_fbc', fbc); /* persist so a later page view still has it */
        }
      } catch (e) {}
    }
    return { fbp: fbp, fbc: fbc };
  }

  function send(event, extra) {
    try {
      var m = meta();
      var body = JSON.stringify(Object.assign({
        event: event, brand: 'amahc', vid: vid(), fbp: m.fbp, fbc: m.fbc,
        page: location.pathname, sid: sessionStorage.getItem('amahc_sid') || undefined
      }, extra || {}));
      /* text/plain keeps this a CORS simple request — sendBeacon cannot set headers, and a
         preflight would drop the hit during page unload. */
      if (navigator.sendBeacon) navigator.sendBeacon(TRACK, new Blob([body], { type: 'text/plain' }));
      else fetch(TRACK, { method: 'POST', body: body, headers: { 'Content-Type': 'text/plain' }, keepalive: true, mode: 'cors' }).catch(function () {});
    } catch (e) {}
  }

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var PHONE_RE = /^[0-9+()\s-]{8,}$/;

  /* Read whatever identity a form is carrying, whoever built it (PowerfulForm, Klaviyo,
     Shopify contact, or a hand-rolled one). Matching on input type/name/placeholder rather
     than a vendor selector means new forms are covered without another code change. */
  function scrapeForm(form) {
    var out = {};
    var inputs = form.querySelectorAll('input, textarea');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      var v = (el.value || '').trim();
      if (!v) continue;
      var hint = ((el.type || '') + ' ' + (el.name || '') + ' ' + (el.id || '') + ' ' + (el.placeholder || '')).toLowerCase();
      if (!out.email && (el.type === 'email' || /e-?mail/.test(hint)) && EMAIL_RE.test(v)) out.email = v;
      else if (!out.phone && (el.type === 'tel' || /phone|mobile|tel/.test(hint)) && PHONE_RE.test(v)) out.phone = v;
      else if (!out.email && EMAIL_RE.test(v)) out.email = v; /* unlabelled field that is plainly an email */
    }
    return out;
  }

  /* Capture-phase listener: fires before a handler that calls preventDefault or re-renders the
     form, which is how most embedded vendor widgets submit. */
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      if (!f || f.tagName !== 'FORM') return;
      var id = scrapeForm(f);
      if (id.email || id.phone) send('chat_lead', id);
    } catch (err) {}
  }, true);

  /* Vendor widgets that never fire a real submit event (LiveChat's iframe, some Klaviyo
     popups) still put the address in an input first — catch it on blur as a fallback. */
  document.addEventListener('blur', function (e) {
    try {
      var el = e.target;
      if (!el || el.tagName !== 'INPUT') return;
      var v = (el.value || '').trim();
      if (el.type === 'email' && EMAIL_RE.test(v)) send('chat_lead', { email: v });
    } catch (err) {}
  }, true);

  /* Any tel: tap anywhere on the site (header, footer, contact page, launcher). The launcher
     posts its own richer call_tap; this covers every other phone link on the site. */
  document.addEventListener('click', function (e) {
    try {
      var a = e.target && e.target.closest ? e.target.closest('a[href^="tel:"]') : null;
      if (a) send('call_tap', { phone_dialled: a.getAttribute('href').replace('tel:', '') });
    } catch (err) {}
  }, true);

  /* One anonymous identity ping per session: the row that gives a vid+fbc pair to a visitor who
     browses today, rings the mainline next week and buys through a rep the week after. */
  try {
    if (!sessionStorage.getItem('amahc_id_ping')) {
      sessionStorage.setItem('amahc_id_ping', '1');
      send('impression', {});
    }
  } catch (e) {}
})();
